// src/admin/AdminApp.jsx
// Standalone admin dashboard, mounted directly for the /admin route (see src/index.js).
// Access is gated on the Firebase `admin` custom claim (see api/set-admin-claim.js) —
// this is a fully separate identity check from normal app auth.

import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { fetchAdminStats } from './adminApi';

const t = {
  bg: '#0f1115', panel: '#171a21', border: '#262b35', text: '#e8eaed',
  textSoft: '#9aa0aa', accent: '#5b9cf6', good: '#3ecf8e', bad: '#f2555a',
};

function Sparkline({ series, color = t.accent, height = 48 }) {
  const max = Math.max(1, ...series.map(p => p.count));
  const w = 300;
  const step = series.length > 1 ? w / (series.length - 1) : w;
  const points = series.map((p, i) => `${i * step},${height - (p.count / max) * height}`).join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${height}`} width="100%" height={height} preserveAspectRatio="none">
      <polyline fill="none" stroke={color} strokeWidth="2" points={points} />
    </svg>
  );
}

function Card({ title, total, sub, children }) {
  return (
    <div style={{ background: t.panel, border: `1px solid ${t.border}`, borderRadius: 12, padding: 16, flex: '1 1 260px', minWidth: 260 }}>
      <div style={{ color: t.textSoft, fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em' }}>{title}</div>
      <div style={{ color: t.text, fontSize: 32, fontWeight: 800, margin: '6px 0 2px' }}>{total}</div>
      {sub && <div style={{ color: t.textSoft, fontSize: 12, marginBottom: 10 }}>{sub}</div>}
      {children}
    </div>
  );
}

function BarList({ rows, labelKey, countKey }) {
  const max = Math.max(1, ...rows.map(r => Number(r[countKey])));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
      {rows.map((r) => (
        <div key={r[labelKey]} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
          <div style={{ width: 90, color: t.textSoft, textTransform: 'capitalize' }}>{r[labelKey]}</div>
          <div style={{ flex: 1, background: '#20242c', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ width: `${(Number(r[countKey]) / max) * 100}%`, background: t.accent, height: 8 }} />
          </div>
          <div style={{ width: 40, textAlign: 'right', color: t.text }}>{r[countKey]}</div>
        </div>
      ))}
    </div>
  );
}

function providerLabel(provider) {
  const p = String(provider || '').toLowerCase();
  if (p.includes('google')) return 'Google';
  if (p.includes('apple')) return 'Apple';
  if (p.includes('password') || p === 'firebase') return 'Email';
  return provider || 'Unknown';
}

export function AdminApp() {
  const { user, firebaseUser, loading, loginWithGoogle, logout } = useAuth();
  const [isAdmin, setIsAdmin] = useState(null); // null = checking
  const [days, setDays] = useState(7);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!firebaseUser) { setIsAdmin(loading ? null : false); return; }
    firebaseUser.getIdTokenResult(true)
      .then((r) => setIsAdmin(r.claims?.admin === true))
      .catch(() => setIsAdmin(false));
  }, [firebaseUser, loading]);

  const load = useCallback((d) => {
    if (!firebaseUser || !isAdmin) return;
    setError('');
    fetchAdminStats(firebaseUser, d).then(setStats).catch((e) => setError(e.message));
  }, [firebaseUser, isAdmin]);

  useEffect(() => {
    const id = setTimeout(() => load(days), 200);
    return () => clearTimeout(id);
  }, [days, load]);

  const shellStyle = { minHeight: '100dvh', background: t.bg, color: t.text, fontFamily: '-apple-system,Segoe UI,sans-serif', padding: 24, boxSizing: 'border-box' };

  if (loading || isAdmin === null) {
    return <div style={shellStyle}>Loading…</div>;
  }

  if (!user) {
    return (
      <div style={{ ...shellStyle, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <h1 style={{ marginBottom: 16 }}>HabiTrak Admin</h1>
          <button onClick={loginWithGoogle} style={{ padding: '12px 24px', borderRadius: 8, border: 'none', background: t.accent, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
            Sign in with Google
          </button>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div style={{ ...shellStyle, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <h1>Access denied</h1>
          <p style={{ color: t.textSoft }}>Signed in as {user.userDetails}, but this account lacks the admin role.</p>
          <button onClick={logout} style={{ marginTop: 12, padding: '10px 20px', borderRadius: 8, border: `1px solid ${t.border}`, background: 'transparent', color: t.text, cursor: 'pointer' }}>
            Sign out
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={shellStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ margin: 0, fontSize: 22 }}>HabiTrak Admin</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <label style={{ fontSize: 13, color: t.textSoft }}>
            Range: <strong style={{ color: t.text }}>{days}d</strong>
          </label>
          <input type="range" min={1} max={180} value={days} onChange={(e) => setDays(Number(e.target.value))} style={{ width: 220 }} />
          <button onClick={logout} style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${t.border}`, background: 'transparent', color: t.text, cursor: 'pointer' }}>
            Sign out
          </button>
        </div>
      </div>

      {error && <div style={{ color: t.bad, marginBottom: 16 }}>Error: {error}</div>}
      {!stats ? <div>Loading stats…</div> : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
          <Card title="Registered users" total={stats.users.total} sub={`last ${days}d trend`}>
            <Sparkline series={stats.users.series} />
          </Card>

          <Card title="Strains" total={stats.strains.total} sub={`+${stats.strains.coaBatchesTotal} verified COA batches`}>
            <Sparkline series={stats.strains.coaBatchesSeries} color={t.good} />
          </Card>

          <Card title="Package scans" total={stats.scans.total} sub={
            stats.scans.workload.countInRange
              ? `${Math.round((stats.scans.workload.successRate || 0) * 100)}% success · avg ${stats.scans.workload.avgDurationMs ?? '–'}ms (${days}d)`
              : `no scans in last ${days}d`
          }>
            <Sparkline series={stats.scans.series} />
          </Card>

          <Card title="COA lookups" total={stats.coaLookups.total} sub={`last ${days}d trend`}>
            <Sparkline series={stats.coaLookups.series} color={t.good} />
            <BarList rows={stats.coaLookups.byKind} labelKey="kind" countKey="count" />
          </Card>

          <Card title="Users by auth type" total={stats.users.total}>
            <BarList
              rows={stats.users.byProvider.map(r => ({ ...r, provider: providerLabel(r.provider) }))}
              labelKey="provider"
              countKey="count"
            />
          </Card>

          <Card title="Check-ins" total={stats.checkins.countInRange} sub={`${stats.checkins.total} all-time · last ${days}d`}>
            <Sparkline series={stats.checkins.series} color={t.good} />
          </Card>

          <Card title="Stash items" total={stats.stash.total} sub="by category, all-time">
            <BarList rows={stats.stash.byCategory} labelKey="category" countKey="count" />
          </Card>
        </div>
      )}
    </div>
  );
}
